import React, { useEffect, useRef } from "react";
import { css } from "@emotion/css";
import { useInView } from "react-intersection-observer";
import VideoRecommendationCard from "../RecommendationFeed/VideoRecommendationCard";
import { ResourceType } from "../RecommendationFeed/types/activity_types";
import { YtSharp } from "../RecommendationFeed/components/YtSharp";
import { Spin } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useBookmarksQuery } from "./hooks/useBookmarksQuery";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60 * 3, // Cache for 3 hours (using gcTime instead of deprecated cacheTime)
    },
  },
});

const BookmarksFeed: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BookmarksFeedContent />
    </QueryClientProvider>
  );
};

const BookmarksFeedContent: React.FC = () => {
  const { data, isLoading, isLoadingMore, hasMore, error, loadMore, removeBookmark, itemsBeingRemoved } =
    useBookmarksQuery();

  // Use a reference to keep track of previous load timing to prevent rapid consecutive loads
  const lastLoadRef = useRef<number>(0);

  // Configure the intersection observer with a threshold and delay
  const { ref: loadMoreRef, inView } = useInView({
    // Only trigger once the element is 300px visible (increased to reduce sensitivity)
    rootMargin: "300px",
    // Only consider element in view when it's 10% visible (helps with partial visibility issues)
    threshold: 0.1,
  });

  // Only use one effect for handling infinite scroll with debounce protection
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadRef.current;

    // Only trigger a new load if:
    // 1. We're looking at the load trigger
    // 2. There are more items to load
    // 3. We're not already loading
    // 4. It's been at least 300ms since our last load (debounce)
    if (
      inView &&
      hasMore &&
      !isLoading &&
      !isLoadingMore &&
      timeSinceLastLoad > 300
    ) {
      lastLoadRef.current = now;
      loadMore();
    }
  }, [inView, hasMore, isLoading, isLoadingMore, loadMore]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          Error loading bookmarks: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>My Bookmarks</h1>

        <div className={styles.list}>
          {data.map((item, index) => (
            <div 
              className={`${styles.listItem} ${itemsBeingRemoved.has(item.id) ? styles.itemRemoving : ''}`} 
              key={`${item.id}`}
            >
              {item.type === ResourceType.YT_FULL_VIDEO ? (
                <VideoRecommendationCard 
                  video={item} 
                  index={index} 
                  onUnbookmark={() => removeBookmark(item.id)}
                  hideMoreOptions={true}
                />
              ) : (
                <YtSharp 
                  recommendation={item} 
                  onUnbookmark={() => removeBookmark(item.id)}
                  hideMoreOptions={true}
                />
              )}
            </div>
          ))}
        </div>

        {/* Show loading state when initially loading or when loading more with no existing data */}
        {(isLoading || isLoadingMore) && data.length === 0 && (
          <div className={styles.message}>
            Loading bookmarks...
            <div style={{ marginTop: "25px" }}>
              <Spin size="large" />
            </div>
          </div>
        )}

        {/* Show empty state only when we're not loading and have no data */}
        {!isLoading && !isLoadingMore && data.length === 0 && (
          <div className={styles.emptyState}>
            No bookmarks yet. Save some content by clicking the bookmark icon!
          </div>
        )}

        {/* Show loading indicator at bottom when loading more items */}
        {isLoadingMore && data.length > 0 && (
          <div className={styles.loadingMore}>
            <Spin size="small" />
            <span style={{ marginLeft: "10px" }}>Loading more...</span>
          </div>
        )}

        {data.length > 0 && (
          <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
            {hasMore ? (
              isLoadingMore && (
                <div className={styles.message}>
                  <Spin size="small" />
                </div>
              )
            ) : (
              <div className={styles.message}>
                You've reached the end of your bookmarks
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const colors = {
  background: "#1a1a1a",
  textPrimary: "#ffffff",
  textSecondary: "#a0a0a0",
  error: "#ff6b6b",
};

const styles = {
  container: css`
    max-width: 800px;
    margin: 0 auto;
    padding: 0 1rem;
    background: ${colors.background};
    min-height: 100vh;
  `,
  content: css`
    padding: 2rem 0;
  `,
  title: css`
    font-size: 1.75rem;
    font-weight: 600;
    margin-bottom: 2rem;
    color: ${colors.textPrimary};
  `,
  list: css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
  `,
  listItem: css`
    width: 100%;
    transition: opacity 0.5s ease, transform 0.5s ease;
  `,
  itemRemoving: css`
    opacity: 0;
    transform: translateY(-30px);
    pointer-events: none;
  `,
  message: css`
    text-align: center;
    padding: 1rem;
    color: ${colors.textSecondary};
    font-size: 0.9rem;
  `,
  loadingMore: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    color: ${colors.textSecondary};
    font-size: 0.9rem;
  `,
  emptyState: css`
    text-align: center;
    padding: 4rem 2rem;
    color: ${colors.textSecondary};
    font-size: 1rem;
    background: ${colors.background};
    border-radius: 8px;
  `,
  errorMessage: css`
    text-align: center;
    color: ${colors.error};
    padding: 2rem;
    font-size: 1rem;
  `,
  loadMoreTrigger: css`
    height: 20px;
    margin: 20px 0;
  `,
};

export default BookmarksFeed;
